/**
 * Device Information Utility
 *
 * Collects device metadata for config API request
 * Matches Xamarin ConfigRequestDTO structure
 */

import { APP_CONFIG, STORAGE_KEYS } from '@/constants';

import type { DeviceInfo } from '@/types';

/**
 * Parse user agent to extract browser/device info
 */
function parseUserAgent(): {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  isMobile: boolean;
} {
  const ua = navigator.userAgent;

  // Detect OS
  let os = 'Unknown';
  let osVersion = 'Unknown';

  if (/Windows NT (\d+\.\d+)/.test(ua)) {
    os = 'Windows';
    const match = /Windows NT (\d+\.\d+)/.exec(ua);
    osVersion = match?.[1] ?? 'Unknown';
  } else if (/Mac OS X (\d+[._]\d+)/.test(ua)) {
    os = 'macOS';
    const match = /Mac OS X (\d+[._]\d+)/.exec(ua);
    osVersion = match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
  } else if (/Android (\d+\.?\d*)/.test(ua)) {
    os = 'Android';
    const match = /Android (\d+\.?\d*)/.exec(ua);
    osVersion = match?.[1] ?? 'Unknown';
  } else if (/iPhone OS (\d+_\d+)/.test(ua) || /iPad.*OS (\d+_\d+)/.test(ua)) {
    os = 'iOS';
    const match = /(?:iPhone|iPad).*OS (\d+_\d+)/.exec(ua);
    osVersion = match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
    osVersion = 'Unknown';
  }

  // Detect browser
  let browser = 'Unknown';
  let browserVersion = 'Unknown';

  if (/Edg\/(\d+\.\d+)/.test(ua)) {
    browser = 'Edge';
    const match = /Edg\/(\d+\.\d+)/.exec(ua);
    browserVersion = match?.[1] ?? 'Unknown';
  } else if (/Chrome\/(\d+\.\d+)/.test(ua)) {
    browser = 'Chrome';
    const match = /Chrome\/(\d+\.\d+)/.exec(ua);
    browserVersion = match?.[1] ?? 'Unknown';
  } else if (/Firefox\/(\d+\.\d+)/.test(ua)) {
    browser = 'Firefox';
    const match = /Firefox\/(\d+\.\d+)/.exec(ua);
    browserVersion = match?.[1] ?? 'Unknown';
  } else if (/Safari\/(\d+\.\d+)/.test(ua) && !ua.includes('Chrome')) {
    browser = 'Safari';
    const match = /Version\/(\d+\.\d+)/.exec(ua);
    browserVersion = match?.[1] ?? 'Unknown';
  }

  // Check if mobile
  const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);

  return { browser, browserVersion, os, osVersion, isMobile };
}

/**
 * Get device make/model from user agent
 */
function getDeviceMakeModel(): { make: string; model: string } {
  const ua = navigator.userAgent;

  // Try to detect specific device
  if (ua.includes('iPhone')) {
    return { make: 'Apple', model: 'iPhone' };
  }
  if (ua.includes('iPad')) {
    return { make: 'Apple', model: 'iPad' };
  }
  if (ua.includes('Macintosh')) {
    return { make: 'Apple', model: 'Mac' };
  }

  // Android devices - try to extract model
  const androidMatch = /;\s*([^;]+)\s+Build/.exec(ua);
  if (androidMatch) {
    const model = androidMatch[1] ?? 'Android Device';
    // Try to extract make from model
    const parts = model.split(' ');
    if (parts.length > 1 && parts[0]) {
      return { make: parts[0], model };
    }
    return { make: 'Android', model };
  }

  // Windows/Linux - use browser as device model
  const { browser, os } = parseUserAgent();
  return { make: os, model: `${browser} Browser` };
}

/**
 * Get current geolocation (with fallback to 0,0)
 */
async function getGeolocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve({ latitude: 0, longitude: 0 });
    }, 5000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        clearTimeout(timeoutId);
        resolve({ latitude: 0, longitude: 0 });
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000, // 5 minutes
      }
    );
  });
}

/**
 * Per-browser install ID — matches Xamarin AppCenter.GetInstallIdAsync().
 * Generated once per browser and persisted in localStorage so the backend
 * creates a unique device record for each browser/device combination.
 */
function getInstallId(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.installId);
    if (stored) return stored;

    const id = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEYS.installId, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Collect all device information for config API request
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const { browserVersion, os, osVersion } = parseUserAgent();
  const { make, model } = getDeviceMakeModel();
  const geo = await getGeolocation();

  return {
    deviceMake: make,
    deviceModel: model,
    deviceOS: os,
    deviceOSVersion: osVersion,
    deviceBuildVersion: browserVersion,
    currentAppVersion: APP_CONFIG.version,
    appCenterId: getInstallId(),
    deviceLatitude: geo.latitude,
    deviceLongitude: geo.longitude,
  };
}

/**
 * Check if running as installed PWA
 */
export function isInstalledPWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-expect-error - iOS Safari specific
    window.navigator.standalone === true
  );
}

/**
 * Check if running on mobile device
 */
export function isMobileDevice(): boolean {
  const { isMobile } = parseUserAgent();
  return isMobile;
}
